import { ReactNode, useRef } from "react";
import cn from "classnames";
import s from "../LogoList.module.scss";
import c from "../../../styles/container.module.scss";
import Logo from "./Logo";
import useContainerWidth from "../../../hooks/useContainerWidth";
import { doLogosFit } from "../utils/calculateLogosWidth";

type LogoItem = {
  _uid: string;
  imageElement: ReactNode;
};

type Props = {
  shouldScroll?: boolean;
  isMobile?: boolean;
  logos: LogoItem[];
  logoSize?: "small" | "medium" | "large";
  transparent?: boolean;
  logoStyle?: "default" | "grayscale" | "blackAndWhite";
  logoOpacity: number;
};

const List = ({
  shouldScroll,
  isMobile = false,
  logos,
  logoSize = "medium",
  transparent = false,
  logoStyle = "default",
  logoOpacity,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);

  // Check if logos fit in the container
  const logosFit =
    containerWidth > 0 && doLogosFit(logos.length, containerWidth, logoSize);

  // Case 1: Desktop + scrollOnDesktop=false → Always show grid
  if (!shouldScroll && !isMobile) {
    return (
      <div ref={containerRef} className={cn(s.listWrapper)}>
        <div className={cn(s.list, c.xxWide)}>
          {logos.map((logo) => (
            <Logo
              key={logo._uid}
              logo={logo.imageElement}
              size={logoSize}
              transparent={transparent}
              logoStyle={logoStyle}
              logoOpacity={logoOpacity}
            />
          ))}
        </div>
      </div>
    );
  }

  // Case 2: Logos fit OR not measured yet (SSR/initial render) → Show single centered row
  if (logosFit || containerWidth === 0) {
    return (
      <div ref={containerRef} className={cn(s.listWrapper)}>
        <div className={cn(s.singleRow, c.xxWide)}>
          {logos.map((logo) => (
            <Logo
              key={logo._uid}
              logo={logo.imageElement}
              size={logoSize}
              transparent={transparent}
              logoStyle={logoStyle}
              logoOpacity={logoOpacity}
            />
          ))}
        </div>
      </div>
    );
  }

  // Case 3: Logos don't fit + scroll enabled → Infinite carousel with CSS animation
  const duplicatedLogos = [...logos, ...logos];

  return (
    <div ref={containerRef} className={cn(s.listWrapper)}>
      <div className={cn(s.marqueeContainer, c.xxWide)}>
        <div className={s.marqueeContent}>
          {duplicatedLogos.map((logo, index) => (
            <Logo
              key={`${logo._uid}-${index}`}
              logo={logo.imageElement}
              size={logoSize}
              transparent={transparent}
              logoStyle={logoStyle}
              logoOpacity={logoOpacity}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default List;
